<template>
  <div>
    <LoadingPanel :loading="loading" :loadingError="loadingError" />
    <div v-if="!loading && !loadingError">
      <ContentTitle>Assembly Options</ContentTitle>

      <div class="row">
        <div class="col-8">
          <FormRow label="Additional Items Enabled" helpTip="If set, additional items can be added to the assembly in order entry">
            <FormCheckbox v-model="assemblyData.isAdditionalItemsEnabled" @change="assemblyUpdated" v-if="editing" />
            <FormStaticLabel v-if="!editing"><IconBoolean :value="assemblyData.isAdditionalItemsEnabled" /></FormStaticLabel>
          </FormRow>
          <FormRow v-if="isSquareMetreUOM" label="Size Editable" helpTip="If set, size of the whole assembly can be set in order entry. Otherwise the assembly is a fixed size">
            <FormCheckbox v-model="assemblyData.isSizeEditable" @change="assemblyUpdated" v-if="editing" />
            <FormStaticLabel v-if="!editing"><IconBoolean :value="assemblyData.isSizeEditable" /></FormStaticLabel>
          </FormRow>
          <FormRow v-if="isSquareMetreUOM" label="Default Size" helpTip="Default size for the assembly. Required if size is not editable" :fieldCount="2">
            <template v-slot:field1>
              <FormInputText type="number" v-model="assemblyData.defaultHeight" @input="assemblyUpdated" prepend="Height" addon="mm" v-if="editing" />
              <FormStaticLabel v-if="!editing">Height: {{assemblyData.defaultHeight}}mm</FormStaticLabel>
            </template>
            <template v-slot:field2>
              <FormInputText type="number" v-model="assemblyData.defaultWidth" @input="assemblyUpdated" prepend="Width" addon="mm" v-if="editing" />
              <FormStaticLabel v-if="!editing">Width: {{assemblyData.defaultWidth}}mm</FormStaticLabel>
            </template>
          </FormRow>
          <FormRow label="Structure Type" helpTip="How the assembly is structured">
            <FormSelect :options="StockAssemblyStructureTypeLookup" v-model="assemblyData.structureType" @input="assemblyUpdated" v-if="editing" />
            <FormStaticLabel v-if="!editing">{{formatLookupName(assemblyData.structureType, StockAssemblyStructureTypeLookup)}}</FormStaticLabel>
          </FormRow>
          <FormRow label="Default Price Unlocked" helpTip="If set, the assembly item will be unlocked and editable by default in order entry">
            <FormCheckbox v-model="assemblyData.isDefaultPriceUnlocked" @change="assemblyUpdated" v-if="editing" />
            <FormStaticLabel v-if="!editing"><IconBoolean :value="assemblyData.isDefaultPriceUnlocked" /></FormStaticLabel>
          </FormRow>
        </div>
      </div>

      <FormHorizontalRule />

      <ContentTitle>Assembly Structure</ContentTitle>
      <div class="stock-assembly-tree">
        <div v-if="editing && isAssemblyEmpty">
          <button class="assembly-item-add-button" v-if="editing" @click.prevent="addItemClicked()">
            <div class="add-button-circle"></div>
            <div class="add-button-text">Add Item</div>
          </button>
        </div>

        <StockAssemblyItem 
          v-for="(item, index) of rootAssemblyItems"
          :key="item.id"
          :editing="editing"
          :assemblyStockItem="stock"
          :rootAssemblyData="assemblyData"
          :assemblyItem="item"
          :firstChild="index == 0"
          :lastChild="index == rootAssemblyItems.length-1"
          @itemClicked="itemClicked"
          @addItem="addItemClicked"
          @deleteClicked="deleteItemClicked"
          @insertClicked="assemblyItemInsertClicked"
          @insertChildClicked="assemblyItemInsertChildClicked"
        />

      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import form from '@/mixins/form.js'
import NestedList from '@/components/table/nestedList.vue'
import StockAssemblyItem from '@/modules/inventory/components/stockAssemblyItem'

import AssemblyItemDialog from '@/modules/inventory/views/assemblyItemDialog'

import { InventoryStockTypes, InventoryStockTypeLookup, UOMTypes, UOMTypeLookup, GlassTypes, GlassTypeTypeLookup, StockAssemblyJoinTypes, StockAssemblyJoinTypeLookup, StockAssemblyStructureTypes, StockAssemblyStructureTypeLookup } from '@/store/types'

export default {
  name: 'StockAssemblyEditor',
  mixins: [form],
  model: {
    event: 'update'
  },
  components: {
    NestedList,
    StockAssemblyItem
  },
  props: {
    value: Object,
    stock: Object,
    editing: Boolean
  },
  watch: {
    value: function() {
      this.assemblyData = _.cloneDeep(this.value || {})
    }
  },
  data() {
    return {
      loading: true,
      loadingError: '',
      currentId: 1,
      stockList: [],
      stockTypeOptions: InventoryStockTypeLookup.filter(lookup => lookup.value != InventoryStockTypes.template),   // Don't add templates
      assemblyData: _.cloneDeep(this.value || {}),
      glassPriceGroupList: []
    }
  },
  computed: {
    glassPriceGroupOptions() {
      return this.$data.glassPriceGroupList.map(priceGroup => {
        return {
          value: priceGroup.id,
          name: priceGroup.name
        }
      })
    },
    uomType() {
      return this.stock.uomType
    },
    isSquareMetreUOM() {
      return this.uomType == UOMTypes.metresq
    },
    nestedAssemblyList() {
      return this.assemblyData.itemList || []
    },
    flattenedItemList() {
      return this.buildFlattenedList()
    },
    rootAssemblyItems() {
      return this.assemblyData.itemList || []
    },
    isAssemblyEmpty() {
      return this.rootAssemblyItems.length == 0
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
    this.StockAssemblyJoinTypeLookup = StockAssemblyJoinTypeLookup
    this.StockAssemblyStructureTypeLookup = StockAssemblyStructureTypeLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    requestData() {
      this.loading = true
      this.loadingError = ''

      Promise.all([
        this.$apiService.pricingService.getStockPriceGroupList(),
        this.$apiService.inventoryService.getStockList()
      ])
      .then(results => {
        this.$data.glassPriceGroupList = results[0].data
        this.$data.stockList = results[1].data.filter(stock => stock.isActive)
      })
      .catch(err => {
        this.loadingError = 'Error loading data'
      })
      .finally(() => {
        this.loading = false
      })
    },
    itemClicked(assemblyItem) {
      var parentItem = this.findItemById(assemblyItem.parentId)

      this.displayModal(AssemblyItemDialog, {
        assemblyUOMType: this.uomType,
        editing: this.editing,
        assemblyItem: assemblyItem,
        parentItem: parentItem
      })
      .then(assemblyItem => {
        if(assemblyItem && this.editing) {
          this.itemUpdated(assemblyItem)
        }
      })
    },
    addItemClicked(parentId = null, listInsertIndex = -1) {
      var parentItem = this.findItemById(parentId)

      this.displayModal(AssemblyItemDialog, {
        assemblyUOMType: this.uomType,
        isCreateMode: true,
        editing: true,
        parentItem: parentItem
      })
      .then(assemblyItem => {
        if(assemblyItem) {

          this.addItemToAssembly(parentId, assemblyItem, listInsertIndex)
          this.assemblyUpdated()
        }
      })
    },
    deleteItemClicked(assemblyItem) {
      this.confirmDeleteMessage('Delete Item?', 'This will delete the item and any child item from the assembly structure')
      .then(confirm => {
        if(confirm) {
          this.removeItem(assemblyItem.id)
        }
      })
    },

    addItemToAssembly(parentId, item, insertIndex = -1) {
      // JB: Ids are UUID strings, but server was converting to null string, so handle both cases
      if(parentId == 'null')
        parentId = null

      item.parentId = parentId
      if(parentId == null) {
        if(insertIndex >= 0)
          this.assemblyData.itemList.splice(insertIndex, 0, item)
        else
          this.assemblyData.itemList.push(item)
      } else {
        var parentItem = this.findItemById(parentId)
        if(parentItem) {
          if(insertIndex >= 0)
            parentItem.childItems.splice(insertIndex, 0, item)
          else
            parentItem.childItems.push(item)
        }
      }
    },
    assemblyItemInsertClicked(assemblyItem) {
      var parentItem = this.findItemById(assemblyItem.parentId)
      var parentList = this.assemblyData.itemList

      if(parentItem)
        parentList = parentItem.childItems

      let insertIndex = parentList.findIndex(item => item.id == assemblyItem.id)
      if(insertIndex != -1) {
        this.addItemClicked(assemblyItem.parentId, insertIndex+1)
      }
    },
    assemblyItemInsertChildClicked(assemblyItem) {
      var parentItem = this.findItemById(assemblyItem.parentId)
      var parentList = this.assemblyData.itemList

      if(parentItem)
        parentList = parentItem.childItems

      let insertIndex = parentList.findIndex(item => item.id == assemblyItem.id)
      if(insertIndex != -1) {
        this.addItemClicked(assemblyItem.parentId, insertIndex)
      }
    },
    addAssemblyRow(stockType) {
      var assemblyItem = this.createAssemblyItem()
      assemblyItem.stockType = stockType
      
      this.assemblyData.itemList.push(assemblyItem)
      this.assemblyUpdated()
    },
    removeItem(itemId) {
      // TODO: Just get parent...
      this._removeTreeItem(itemId, this.assemblyData.itemList)
      
      this.assemblyUpdated()
    },
    addChildItem(parentItemId, stockType) {
      var newItem = this.createAssemblyItem()
      newItem.stockType = stockType

      let parentItem = this.findItemById(parentItemId)
      if(parentItem)
        parentItem.childItems.push(newItem)

      this.assemblyUpdated()
    },
    findItemById(id, list = null) {
      if(list == null)
        list = this.assemblyData.itemList

      var item = list.find(listItem => listItem.id == id)
      if(item)
        return item

      for(let listItem of list) {
        if(listItem.childItems) {
          item = this.findItemById(id, listItem.childItems)
          if(item)
            return item
        }
      }
      return null
    },
    _removeTreeItem(itemId, itemList) {
      var index = itemList.findIndex(item => item.id == itemId)

      if(index != -1) {
        itemList.splice(index, 1)
      } else {
        itemList.forEach(item => {
          if(item.childItems && item.childItems.length > 0) {
            this._removeTreeItem(itemId, item.childItems)
          }
        })
      }      
    },
    createAssemblyItem(parentId = null) {
      return {
        id: uuidv4(),
        description: '',
        parentId: parentId,
        stockType: InventoryStockTypes.glass,
        stockList: [],
        isUnitPriceOverridden: false,
        unitPrice: 0,
        pricingGroupList: [],
        quantity: 1,
        edgeOffsetList: [0, 0, 0, 0],
        edgeworkFlagList: [],
        joinType: StockAssemblyJoinTypes.corner,
        cornerRadius: 0,
        childItems: []
      }
    },
    assemblyUpdated() {
      this.$emit('update', this.assemblyData)
    },
    itemUpdated(item) {
      // Apply item to this assembly from nested copy
      var assItem = this.findItemById(item.id)
      if(assItem)
        Object.assign(assItem, item)

      this.assemblyUpdated()
    },
    itemListUpdated() {
      this.$emit('update', this.assemblyData)
    },
    isItemGlass(item) {
      return item.stockType == InventoryStockTypes.glass
    },
    isItemAssembly(item) {
      return item.stockType == InventoryStockTypes.assembly
    },
    isItemGeneral(item) {
      return item.stockType == InventoryStockTypes.general
    },
    isItemLengthUoM(item) {
      // At least one of the stock items must be length UoM
      var stockItems = _.compact(item.stockList.map(stockId => {
        return this.stockList.find(stock => stock.id == stockId) 
      }))

      return stockItems.some(stock => stock.uomType == UOMTypes.length)
    },
    isItemEdgeworkService(item) {
      if(item.stockType != InventoryStockTypes.glassService)
        return false

      return this.isItemLengthUoM(item)
    },
    isItemEdgeOffsets(item) {
      if(!this.isSquareMetreUOM)
        return false

      if(this.isItemGlass(item))
        return true

      if(this.isItemGeneral(item)) {
        return this.isItemLengthUoM(item)
      }
      return false
    },
    isItemJoinTypeRounded(item) {
      return item.joinType == StockAssemblyJoinTypes.rounded
    },
    stockListForType(stockType) {
      return this.stockList.filter(stock => stock.type == stockType).map(stock => {
        return {
          value: stock.id,
          name: `${stock.code} | ${stock.name}`
        }
      })
    },
    stockCodeLookup(templateItem) {
      var name = this.getLookupName(this.stockListForType(templateItem.stockType), templateItem.stockId)
      if(name == null || name == '')
        return 'Empty'
      return name
    },
    buildFlattenedList(itemList = null, level = 0) {
      if(itemList == null)
        itemList = (this.assemblyData.itemList || [])

      var outputList = []
      itemList.forEach(item => {
        let outputItem = _.clone(item)
        outputItem.level = level
        outputList.push(outputItem)

        if(item.childItems)
          outputList = _.concat(outputList, this.buildFlattenedList(item.childItems, level + 1))
      })

      return outputList
    }
  }
}
</script>